import { Tooltip } from '@economic/taco';
import React from 'react';
import { FormSpy } from 'react-final-form';
import { Field } from '../../../../../shared/components/Form';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import CustomerContactsCombobox from '../../../../../shared/components/ui/CustomerContactsCombobox';
import LoadingBox from '../../../../home/PlanUpgradeDialog/LoadingBox';
import { SendElectronicInvoiceMethod } from '../../invoices/book-and-send/types';

const getSendingMethods = (canSendViaPeppol: boolean) => {
    const sendingMethods = [
        {
            text: t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_CVR`,
            value: SendElectronicInvoiceMethod.CvrNumber,
        },
        {
            text: t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_GLN`,
            value: SendElectronicInvoiceMethod.GlnNumber,
        },
        {
            text: t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_PNUMBER`,
            value: SendElectronicInvoiceMethod.PNumber,
        },
    ];

    if (canSendViaPeppol) {
        sendingMethods.splice(2, 0, {
            text: t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_PEPPOLID`,
            value: SendElectronicInvoiceMethod.PeppolId,
        });
    }

    return sendingMethods;
};

interface SendEinvoiceOptionsDrawerFragmentProps {
    name: string;
    value: any;
    customerNumber: number;
    isCvrReadOnly: boolean;
    canSendViaPeppol: boolean;
    validCustomerContactIds: number[];
}

function SendEinvoiceOptionsDrawerFragment({
    name,
    value,
    customerNumber,
    isCvrReadOnly,
    canSendViaPeppol,
    validCustomerContactIds,
}: SendEinvoiceOptionsDrawerFragmentProps) {
    return (
        <>
            <Field.Dropdown
                id="sendingMethod"
                label={t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_SENDING_METHOD`}
                name={`${name}.sendingMethod`}
                required
                options={getSendingMethods(canSendViaPeppol)}
            />

            {value.sendingMethod === SendElectronicInvoiceMethod.GlnNumber && <Field.Text name={`${name}.gln`} required />}

            {value.sendingMethod === SendElectronicInvoiceMethod.CvrNumber && (
                <Field.Text name={`${name}.cvr`} required readOnly={isCvrReadOnly} />
            )}

            {value.sendingMethod === SendElectronicInvoiceMethod.PeppolId && (
                <>
                    <Field.Text
                        name={`${name}.cvr`}
                        label={t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_CVR`}
                        required
                        readOnly={isCvrReadOnly}
                    />
                    <Field.Text
                        name={`${name}.customerCountry`}
                        label={t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_CUSTOMERCOUNTRY`}
                        readOnly
                    />
                    <Tooltip title={t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_PEPPOLID_TOOLTIP`}>
                        <div>
                            <Field.Text
                                name={`${name}.peppolId`}
                                label={t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_PEPPOLID`}
                                required
                                readOnly
                            />
                        </div>
                    </Tooltip>
                </>
            )}

            {value.sendingMethod === SendElectronicInvoiceMethod.PNumber && <Field.Text name={`${name}.pNumber`} required />}

            <Field.Combobox
                id="yourRef"
                label={t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_YOURREF`}
                name={`${name}.yourRef`}
                as={CustomerContactsCombobox}
                customerNumber={customerNumber}
                validate={value => {
                    if (value && validCustomerContactIds?.includes(value))
                        return t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_WRONG_NUMBER_YOURREF_OR_ATTENTIONREF`;
                }}
            />
            <Field.Combobox
                id="attentionRef"
                label="Attention"
                name={`${name}.attentionRef`}
                as={CustomerContactsCombobox}
                customerNumber={customerNumber}
                validate={value => {
                    if (value && validCustomerContactIds?.includes(value))
                        return t`FORM_BOOKANDSEND_EINVOICE_OPTIONS_WRONG_NUMBER_YOURREF_OR_ATTENTIONREF`;
                }}
            />
        </>
    );
}

interface SendEinvoiceOptionsDrawerProps {
    customerNumber: number;
    isCvrReadOnly: boolean;
    disableDrawer: boolean;
    canSendViaPeppol: boolean;
}

export function SendEinvoiceOptionsDrawer({
    customerNumber,
    isCvrReadOnly,
    disableDrawer,
    canSendViaPeppol,
}: SendEinvoiceOptionsDrawerProps) {
    const loadingTexts = useLocalization(textIds);

    if (loadingTexts || disableDrawer) {
        return <LoadingBox />;
    }

    return (
        <div className="book-and-send-dialog__einvoice-options">
            <FormSpy subscription={{ values: true }}>
                {() => (
                    <Field.Fragment
                        name="eInvoiceOptions"
                        component={SendEinvoiceOptionsDrawerFragment}
                        customerNumber={customerNumber}
                        isCvrReadOnly={isCvrReadOnly}
                        canSendViaPeppol={canSendViaPeppol}
                    />
                )}
            </FormSpy>
        </div>
    );
}

const textIds = [
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_YOURREF',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_SENDING_METHOD',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_GLN',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_CVR',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_PEPPOLID',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_PEPPOLID_TOOLTIP',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_PNUMBER',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_CUSTOMERCOUNTRY',
    'MISSING_GLN',
    'MISSING_CVR',
    'MISSING_PNUMBER',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_ERROR_YOURREF_OR_ATTENTIONREF',
    'FORM_BOOKANDSEND_EINVOICE_OPTIONS_WRONG_NUMBER_YOURREF_OR_ATTENTIONREF',
];
